import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Typography,
  Grid, Card, CardContent, Chip, Divider,
  CircularProgress, Alert, Avatar, List, ListItem,
  ListItemText, ListItemAvatar
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { attachFeatureToPlane, getAllFeatures } from "../../../Services/FeaturesApi";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FeaturedPlayListIcon from "@mui/icons-material/FeaturedPlayList";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NumbersIcon from "@mui/icons-material/Numbers";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import CategoryIcon from "@mui/icons-material/Category";

const AddFeaturesDialog = ({ open, onClose, plane_id, onSuccess, selectedPlanFeatures }) => {
  const [form, setForm] = useState({
    feature_id: "",
    value: "",
    yearly_cost: "",
    monthly_cost: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [featuresList, setFeaturesList] = useState([]);
  const [selectedType, setSelectedType] = useState(""); // نوع الفيتشر المختار
  const [selectedKey, setSelectedKey] = useState(""); // مفتاح الفيتشر المختار

  // مجموعة معرفات المميزات المفعّلة (كـ string لتفادي اختلاف الأنواع)
  const activeIds = new Set(
    (selectedPlanFeatures || [])
      .map((f) => String(f?.id ?? f?.pivot?.feature_id ?? ""))
      .filter(Boolean)
  );

  useEffect(() => {
    if (open) {
      getAllFeatures().then((data) => {
        // فلتر لإخفاء ميزة cashboxes_banks
        const filteredFeatures = (data.features || []).filter(
          feature => feature.key !== "cashboxes_banks"
        );
        setFeaturesList(filteredFeatures);
      });
    }
  }, [open]);

  // تحديث نوع الفيتشر عند تغييره
  useEffect(() => {
    if (!form.feature_id) {
      setSelectedType("");
      setSelectedKey("");
      return;
    }
    const selected = featuresList.find(f => f.id === Number(form.feature_id) || f.id === form.feature_id);
    setSelectedType(selected ? selected.type : "");
    setSelectedKey(selected ? (selected.key || "") : "");
  }, [form.feature_id, featuresList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // إذا كانت القيمة، تحقق من النوع
    if (name === "value") {
      if (selectedKey === "database_backup") {
        // تقبل فقط 0 أو 1 أو 2
        if (value !== "" && !["0","1","2"].includes(String(value))) return;
      } else if (selectedType === "boolean") {
        // تقبل فقط 0 أو 1
        if (value !== "" && value !== "0" && value !== "1") return;
      } else if (selectedType === "numeric") {
        // تقبل فقط أرقام
        if (value !== "" && !/^\d*$/.test(value)) return;
      }
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await attachFeatureToPlane({
        plane_id,
        ...form
      });
      // لا نغلق الديالوج هنا — نعلم المكوّن الأب ليتعامل مع التحديث ويترك الديالوج مفتوحاً
      if (onSuccess) await onSuccess();
      setForm({
        feature_id: "",
        value: "",
        yearly_cost: "",
        monthly_cost: ""
      });
    } catch (err) {
      // يمكنك إضافة معالجة الأخطاء هنا
      console.error(err);
    }
    setSubmitting(false);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pb: 2
      }}>
        <AddCircleIcon sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            إضافة فيتشر للخطة
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            اختر الفيتشر وحدد التكلفة
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* عرض الفيتشرات المرتبطة بالخطة */}
        {selectedPlanFeatures && selectedPlanFeatures.length > 0 && (
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 3, 
              bgcolor: '#f8f9fa',
              border: '2px solid #e3f2fd'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FeaturedPlayListIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  الفيتشرات الحالية ({selectedPlanFeatures.length})
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List sx={{ pt: 0 }}>
                {selectedPlanFeatures.map((f, i) => (
                  <ListItem 
                    key={i}
                    sx={{ 
                      bgcolor: 'white',
                      mb: 1,
                      borderRadius: 1,
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#2196f3' }}>
                        <CheckCircleIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {f.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={`${f.key}`} size="small" variant="outlined" />
                          <Chip label={`قيمة: ${f.pivot?.value}`} size="small" color="primary" />
                          <Chip label={`شهري: ${f.pivot?.monthly_cost}`} size="small" color="info" />
                          <Chip label={`سنوي: ${f.pivot?.yearly_cost}`} size="small" color="secondary" />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CategoryIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      اختيار الفيتشر
                    </Typography>
                  </Box>
                  <Autocomplete
                    options={featuresList || []}
                    getOptionLabel={(opt) => opt.name || String(opt.id)}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    value={(featuresList || []).find(f => String(f.id) === String(form.feature_id)) || null}
                    onChange={(e, newVal) => {
                      setForm((prev) => ({ ...prev, feature_id: newVal ? newVal.id : "" }));
                    }}
                    disableClearable={false}
                    fullWidth
                    getOptionDisabled={(option) => activeIds.has(String(option.id))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="اختر الفيتشر"
                        required
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const isActive = activeIds.has(String(option.id));
                      return (
                        <li {...props} key={option.id} aria-disabled={isActive}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <FeaturedPlayListIcon fontSize="small" color="primary" />
                            <Box sx={{ flex: 1 }}>{option.name}</Box>
                            <Chip label={option.key} size="small" variant="outlined" />
                            <Chip label={option.type} size="small" color="secondary" />
                            {isActive && <Chip label="مفعل" size="small" color="success" sx={{ ml: 1 }} />}
                          </Box>
                        </li>
                      );
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {selectedType === "boolean" ? (
                      <ToggleOnIcon color="success" />
                    ) : (
                      <NumbersIcon color="primary" />
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {selectedType === "boolean" ? "حالة التفعيل" : "القيمة/العدد"}
                    </Typography>
                  </Box>
                  {selectedType === "boolean" ? (
                    // لو المفتاح database_backup اعرض ثلاث خيارات 0/1/2
                    selectedKey === "database_backup" ? (
                      <TextField
                        select
                        label="حالة النسخ الاحتياطي"
                        name="value"
                        value={form.value}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                        helperText="اختر 0 أو 1 أو 2"
                      >
                        <MenuItem value="0">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>0 - نسخ احطياطي يومي</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="1">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>1 - احتياطي يومي</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="2">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>2 - احتياطي أسبوعي</Typography>
                          </Box>
                        </MenuItem>
                      </TextField>
                    ) : (
                      <TextField
                        select
                        label="تفعيل ؟"
                        name="value"
                        value={form.value}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                        helperText="اختر 1 (نعم) أو 0 (لا)"
                      >
                        <MenuItem value="1">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon color="success" />
                            نعم (مفعّل)
                          </Box>
                        </MenuItem>
                        <MenuItem value="0">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon color="disabled" />
                            لا (غير مفعّل)
                          </Box>
                        </MenuItem>
                      </TextField>
                    )
                   ) : (
                    // لو المفتاح database_backup وأنت هنا لسبب ما، fallback إلى select ثلاثي القيم
                    selectedKey === "database_backup" ? (
                      <TextField
                        select
                        label="حالة النسخ الاحتياطي"
                        name="value"
                        value={form.value}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                        helperText="اختر 0 أو 1 أو 2"
                      >
                        <MenuItem value="0"><Typography>0 - نسخ احطياطي يومي</Typography></MenuItem>
                        <MenuItem value="1"><Typography>1 - نسخ احتياطي اسبوعي</Typography></MenuItem>
                        <MenuItem value="2"><Typography>2 - نسخ احتياطي شهري</Typography></MenuItem>
                      </TextField>
                    ) : (
                      <TextField
                        label="العدد أو القيمة"
                        name="value"
                        value={form.value}
                        onChange={handleChange}
                        fullWidth
                        required
                        type={selectedType === "numeric" ? "number" : "text"}
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                        helperText={selectedType === "numeric" ? "أدخل رقم فقط" : "أدخل القيمة المطلوبة"}
                      />
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoneyIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      التكلفة الشهرية
                    </Typography>
                  </Box>
                  <TextField
                    label="التكلفة الشهرية (جنيه)"
                    name="monthly_cost"
                    value={form.monthly_cost}
                    onChange={handleChange}
                    fullWidth
                    required
                    type="number"
                    variant="outlined"
                    sx={{ bgcolor: 'white' }}
                    InputProps={{
                      startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: '#1976d2' }} />
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoneyIcon color="secondary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                      التكلفة السنوية
                    </Typography>
                  </Box>
                  <TextField
                    label="التكلفة السنوية (جنيه)"
                    name="yearly_cost"
                    value={form.yearly_cost}
                    onChange={handleChange}
                    fullWidth
                    required
                    type="number"
                    variant="outlined"
                    sx={{ bgcolor: 'white' }}
                    InputProps={{
                      startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: '#9c27b0' }} />
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#f5f5f5' }}>
        <Button 
          onClick={onClose} 
          disabled={submitting}
          variant="outlined"
          size="large"
          sx={{ minWidth: 100 }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting}
          size="large"
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AddCircleIcon />}
          sx={{ 
            minWidth: 120,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976d2 30%, #1e88e5 90%)',
            }
          }}
        >
          {submitting ? "جاري الإضافة..." : "إضافة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFeaturesDialog;
